import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { trackContentInteraction } from '../utils/gtm';
import scrollRestoration from '../utils/scroll-restoration';
import { createStableKey } from '../utils/deduplication';
import { getShowingText } from '../utils/lithuanian-grammar';
import { useScrollRestoration } from '../hooks/use-scroll-restoration';
import { useLanguage } from '../contexts/language-context';
import { 
  getMediaTitle as getTitle,
  getMediaReleaseDate as getDate,
  isTVShow,
  TMDB_IMAGE_URLS,
  SCROLL_THRESHOLDS,
  ANIMATION_DELAYS
} from '../constants';
import { getRatingData, getYear, getGenreNames } from '../helpers';
import { SPINNER_CLASSES, BUTTON_CLASSES } from '../styles';

export { getRatingData, getYear, getGenreNames };

function PosterFallback({ title, mediaType = 'movie' }) {
  const icon = isTVShow(mediaType) ? (
    <path d="M12 2C8.13 2 5 5.13 5 9c0 1.74.5 3.37 1.41 4.84.95 1.54 2.2 2.86 3.16 4.4.47.75.75 1.6.43 2.76H10c-1.1 0-2 .9-2 2s.9 2 2 2h4c1.1 0 2-.9 2-2s-.9-2-2-2h-.79c-.32-1.16-.04-2.01.43-2.76.96-1.54 2.21-2.86 3.16-4.4C18.5 12.37 19 10.74 19 9c0-3.87-3.13-7-7-7zm0 2c2.76 0 5 2.24 5 5 0 1.64-.68 3.06-1.39 4.24-.75 1.26-1.49 2.69-1.49 4.76 0 .34-.03.68-.12 1H10c-.09-.32-.12-.66-.12-1 0-2.07-.74-3.5-1.49-4.76C7.68 12.06 7 10.64 7 9c0-2.76 2.24-5 5-5z"/>
  ) : (
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 12c.83 0 1.5-.67 1.5-1.5S9.83 9 9 9s-1.5.67-1.5 1.5S8.17 12 9 12zm3 5L9 14l-3 4h12l-4.5-6L12 17z"/>
  );
  
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex flex-col items-center justify-center border border-gray-600">
      <div className="p-4 text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
            {icon}
          </svg>
        </div>
        <p className="text-xs text-gray-400 font-medium truncate">{title}</p>
      </div>
    </div>
  );
}

function RatingBadges({ ratingData }) {
  if (!ratingData.imdb && !ratingData.rt && !ratingData.metacritic && !ratingData.userRating) {
    return null;
  }

  return (
    <div className="absolute top-2 left-2 flex flex-col items-start space-y-1">
      {ratingData.imdb && (
        <div className="flex items-center space-x-1 bg-yellow-600/20 px-2 py-1 rounded">
          <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="text-yellow-400 font-semibold text-xs">{ratingData.imdb}</span>
          <span className="text-yellow-300 text-xs font-medium">IMDB</span>
        </div>
      )}
      
      {ratingData.rt && (
        <div className={`flex items-center space-x-1 px-2 py-1 rounded ${
          ratingData.rt >= 60 ? 'bg-red-600/20' : 'bg-green-600/20'
        }`}>
          <svg className={`w-3 h-3 ${ratingData.rt >= 60 ? 'text-red-400' : 'text-green-400'}`} 
               fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
          </svg>
          <span className={`font-semibold text-xs ${
            ratingData.rt >= 60 ? 'text-red-400' : 'text-green-400'
          }`}>
            {ratingData.rt}%
          </span>
          <span className="text-gray-300 text-xs font-medium">RT</span>
        </div>
      )}

      {ratingData.metacritic && (
        <div className="flex items-center space-x-1 bg-purple-600/20 px-2 py-1 rounded">
          <svg className="w-3 h-3 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="text-purple-400 font-semibold text-xs">{ratingData.metacritic}</span>
          <span className="text-purple-300 text-xs font-medium">Metacritic</span>
        </div>
      )}

      {ratingData.userRating && (
        <div className="flex items-center space-x-1 bg-blue-600/20 px-2 py-1 rounded">
          <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="text-blue-400 font-semibold text-xs">{ratingData.userRating}</span>
        </div>
      )}
    </div>
  );
}

export function MediaCard({ media, mediaType, searchQuery, filters, locationKey }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const imageBaseUrl = TMDB_IMAGE_URLS.W500;
  const title = getTitle(media, mediaType);
  const dateString = getDate(media, mediaType);
  const ratingData = getRatingData(media);
  const genreNames = getGenreNames(media.genre_ids, mediaType);

  const handleCardClick = () => {
    if (locationKey) {
      scrollRestoration.saveScrollPosition(locationKey, window.scrollY);
      scrollRestoration.setNavigatingToDetail(locationKey);
    }
    
    trackContentInteraction('view', mediaType, {
      id: media.id,
      title: title,
      genre: media.genre_ids?.[0] || 'unknown',
      rating: media.vote_average || 0,
      year: getYear(dateString),
      from_search: !!searchQuery
    });
    
    navigate(`/${mediaType}/${media.id}`);
  };

  return (
    <div 
      className="group relative card movie-card transform transition-all duration-300 hover:scale-[1.02] animate-fade-in cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="movie-poster relative overflow-hidden bg-dark-700">
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 animate-pulse flex items-center justify-center border border-gray-600">
            <div className="text-center p-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-xs text-gray-400">Loading...</p>
            </div>
          </div>
        )}
        
        {imageError || !media.poster_path ? (
          <PosterFallback title={title} mediaType={mediaType} />
        ) : (
          <img
            src={`${imageBaseUrl}${media.poster_path}`}
            alt={title}
            className={`w-full h-full object-cover transition-all duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            loading="lazy"
          />
        )}

        <RatingBadges ratingData={ratingData} />
      </div>

      <div className="movie-info space-y-2">
        <h3 className="movie-title font-bold text-white text-lg leading-tight line-clamp-2 group-hover:text-primary-400 transition-colors">
          {title}
        </h3>
        
        <div className="movie-meta text-sm">
          <span className="text-gray-400">{getYear(dateString)}</span>
        </div>

        <p className="movie-description text-gray-400 text-sm line-clamp-2 leading-relaxed">
          {media.overview && media.overview.trim() ? media.overview : t('noDescription')}
        </p>

        {genreNames.length > 0 && (
          <div className="movie-genres flex flex-wrap gap-1">
            {genreNames.map((genre, index) => (
              <span
                key={`${media.id}-genre-${index}`}
                className="text-xs px-2 py-1 rounded-full bg-dark-700 text-gray-300"
              >
                {genre}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function MediaListItem({ media, mediaType, searchQuery, filters, locationKey }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const imageBaseUrl = TMDB_IMAGE_URLS.W342;
  
  const title = getTitle(media, mediaType);
  const dateString = getDate(media, mediaType);
  const ratingData = getRatingData(media);
  const genreNames = getGenreNames(media.genre_ids, mediaType);

  const handleClick = () => {
    if (locationKey) {
      scrollRestoration.saveScrollPosition(locationKey, window.scrollY);
      scrollRestoration.setNavigatingToDetail(locationKey);
    }
    navigate(`/${mediaType}/${media.id}`);
  };

  return (
    <div className="list-item" onClick={handleClick}>
      <div className="list-poster">
        {!imageLoaded && !imageError && media.poster_path && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 animate-pulse flex items-center justify-center border border-gray-600">
            <div className="text-center p-2">
              <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-xs text-gray-400">Loading...</p>
            </div>
          </div>
        )}
        
        {imageError || !media.poster_path ? (
          <PosterFallback title={title} mediaType={mediaType} />
        ) : (
          <img
            src={`${imageBaseUrl}${media.poster_path}`}
            alt={title}
            className={`transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
      </div>

      <div className="list-content">
        <div>
          <h3 className="list-title hover:text-primary-400 transition-colors">{title}</h3>
          
          <div className="list-meta">
            <span>{getYear(dateString)}</span>
            
            {ratingData.imdb && (
              <>
                <span>•</span>
                <div className="flex items-center space-x-1 bg-yellow-600/20 px-2 py-1 rounded">
                  <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-yellow-400 font-semibold text-xs">{ratingData.imdb}</span>
                  <span className="text-yellow-300 text-xs">IMDB</span>
                </div>
              </>
            )}
            
            {ratingData.rt && (
              <>
                <span>•</span>
                <div className={`flex items-center space-x-1 px-2 py-1 rounded ${
                  ratingData.rt >= 60 ? 'bg-red-600/20' : 'bg-green-600/20'
                }`}>
                  <svg className={`w-3 h-3 ${ratingData.rt >= 60 ? 'text-red-400' : 'text-green-400'}`} 
                       fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                  <span className={`font-semibold text-xs ${
                    ratingData.rt >= 60 ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {ratingData.rt}%
                  </span>
                  <span className="text-gray-300 text-xs">RT</span>
                </div>
              </>
            )}

            {ratingData.metacritic && (
              <>
                <span>•</span>
                <div className="flex items-center space-x-1 bg-purple-600/20 px-2 py-1 rounded">
                  <svg className="w-3 h-3 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-purple-400 font-semibold text-xs">{ratingData.metacritic}</span>
                  <span className="text-purple-300 text-xs">Metacritic</span>
                </div>
              </>
            )}

            {ratingData.userRating && (
              <>
                <span>•</span>
                <div className="flex items-center space-x-1 bg-blue-600/20 px-2 py-1 rounded">
                  <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-blue-400 font-semibold text-xs">{ratingData.userRating}</span>
                </div>
              </>
            )}
          </div>

          <p className="list-description">
            {media.overview && media.overview.trim() ? media.overview : t('noDescription')}
          </p>
        </div>

        {genreNames.length > 0 && (
          <div className="list-genres mt-3">
            {genreNames.map((genre, index) => (
              <span key={`${media.id}-genre-${index}`} className="list-genre-tag">
                {genre}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function MediaList({
  mediaType,
  searchQuery,
  filters,
  viewMode = 'grid',
  items,
  totalCount,
  isLoading,
  isFetchingNextPage,
  isTransitioning = false,
  hasNextPage,
  fetchNextPage,
  error,
  isError,
  isRateLimited,
  refetch,
  scrollRestorationMediaType,
  useItemMediaType = false
}) {
  const restorationMediaType = scrollRestorationMediaType ?? mediaType;

  const locationKey = useScrollRestoration({
    mediaType: restorationMediaType,
    searchQuery,
    filters,
    isLoading,
    itemsLength: items.length
  });

  const resolveCardMediaType = (item) => {
    if (!useItemMediaType) return mediaType;
    return item.media_type === 'tv' ? 'tv' : 'movie';
  };

  const labels = {
    movie: {
      title: 'filmai',
      titleCapital: 'Filmai',
      loading: 'Kraunami filmai...',
      error: 'Klaida kraunant filmus',
      notFound: 'Filmų nerasta',
      notFoundSearch: (query) => `Pagal "${query}" filmų nerasta. Pabandykite keisti paieškos kriterijus.`,
      notFoundFilter: 'Su šiais filtrais filmų nerasta. Pabandykite pakeisti filtrus.',
      viewedAll: 'Jūs peržiūrėjote visus filmus!',
      showing: (current, total) => getShowingText(current, 'movie')
    },
    tv: {
      title: 'TV serialai',
      titleCapital: 'TV Serialai',
      loading: 'Kraunami TV serialai...',
      error: 'Klaida kraunant TV serialus',
      notFound: 'TV serialų nerasta',
      notFoundSearch: (query) => `Pagal "${query}" TV serialų nerasta. Pabandykite keisti paieškos kriterijus.`,
      notFoundFilter: 'Su šiais filtrais TV serialų nerasta. Pabandykite pakeisti filtrus.',
      viewedAll: 'Jūs peržiūrėjote visus TV serialus!',
      showing: (current, total) => getShowingText(current, 'tv')
    }
  };

  const label = labels[mediaType];

  if (isError) {
    const isRateLimitedError = error?.message?.includes('rate limit') || 
                               error?.response?.status === 429 ||
                               error?.response?.status === 503 ||
                               error?.message?.includes('error code: 1015');
    
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <div className="text-red-400 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isRateLimitedError ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <h3 className="text-xl font-semibold text-white mb-2">
              {isRateLimitedError ? 'Laikinas apribojimas' : label.error}
            </h3>
            <p className="text-gray-400 mb-4">
              {isRateLimitedError 
                ? 'Duomenų šaltinis laikinai apribojo užklausas. Pabandykite už kelių sekundžių.' 
                : (error?.message || 'Įvyko nežinoma klaida')
              }
            </p>
            <button
              onClick={() => refetch()}
              className={BUTTON_CLASSES.PRIMARY_SM}
            >
              {isRateLimitedError ? 'Pabandyti dar kartą' : 'Bandyti dar kartą'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center py-12">
          <div className={SPINNER_CLASSES.XLARGE_PRIMARY}></div>
          <span className="ml-3 text-gray-400">{label.loading}</span>
        </div>
      </div>
    );
  }

  if (!items.length) {
    if (isRateLimited) {
      return (
        <div className="space-y-8">
          <div className="flex items-center justify-center py-12">
            <div className={SPINNER_CLASSES.XLARGE_PRIMARY}></div>
            <span className="ml-3 text-gray-400">Kraunami duomenys...</span>
          </div>
        </div>
      );
    }

    const isPossiblyServerIssue = totalCount > 0 && !isRateLimited;
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 110 2h-1v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 110-2h4z" />
            </svg>
            {isPossiblyServerIssue ? (
              <>
                <h3 className="text-xl font-semibold text-white mb-2">Paslauga laikinai nepasiekiama</h3>
                <p className="text-gray-400">Mūsų serveriai šiuo metu apkrauti. Pabandykite dar kartą po kelių minučių.</p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-white mb-2">{label.notFound}</h3>
                <p className="text-gray-400">
                  {searchQuery.trim() ? label.notFoundSearch(searchQuery) : label.notFoundFilter}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Subtle transition indicator when filters are being deferred */}
      {isTransitioning && (
        <div className="flex items-center justify-center py-2 -my-4">
          <div className="flex items-center space-x-2 text-primary-400/80 text-sm animate-pulse">
            <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            <span className="ml-1 text-xs font-medium">Atnaujinama...</span>
          </div>
        </div>
      )}

      <InfiniteScroll
        dataLength={items.length}
        next={fetchNextPage}
        hasMore={hasNextPage || false}
        loader={
          <div className="flex items-center justify-center space-x-2 text-gray-400 py-8">
            <div className={SPINNER_CLASSES.LARGE_PRIMARY}></div>
            <span>{label.loading}</span>
          </div>
        }
        endMessage={
          <div className="flex items-center justify-center space-x-2 text-gray-400 text-sm py-8">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>{label.viewedAll}</span>
          </div>
        }
        scrollThreshold={SCROLL_THRESHOLDS.INFINITE_SCROLL}
      >
        {viewMode === 'grid' ? (
          <div className="grid-uniform">
            {items.map((item, index) => {
              const cardType = resolveCardMediaType(item);
              return (
              <div 
                key={createStableKey(item, cardType, index)} 
                className="animate-slide-up h-full" 
                style={{ animationDelay: `${(index % 20) * ANIMATION_DELAYS.CARD_STAGGER}ms` }}
              >
                <MediaCard 
                  media={item} 
                  mediaType={cardType}
                  searchQuery={searchQuery} 
                  filters={filters} 
                  locationKey={locationKey} 
                />
              </div>
              );
            })}
          </div>
        ) : (
          <div className="list-uniform">
            {items.map((item, index) => {
              const cardType = resolveCardMediaType(item);
              return (
              <div 
                key={createStableKey(item, cardType, index)} 
                className="animate-slide-up" 
                style={{ animationDelay: `${(index % 20) * ANIMATION_DELAYS.LIST_ITEM_STAGGER}ms` }}
              >
                <MediaListItem 
                  media={item} 
                  mediaType={cardType}
                  searchQuery={searchQuery} 
                  filters={filters} 
                  locationKey={locationKey} 
                />
              </div>
              );
            })}
          </div>
        )}
      </InfiniteScroll>

      {isFetchingNextPage && (
        <div className="text-center text-gray-400 text-sm py-4">
          {label.showing(items.length, totalCount)}
        </div>
      )}
    </div>
  );
}

export default MediaList;
